import type { CustomerMapping } from '@/types/customerMapping';

// A PO carries a customer code only when it ends in exactly two ASCII letters;
// the code is the LAST of those letters (e.g. PO00042AB → B). The lookbehind
// rejects longer letter runs (PO42ABC), and a lone trailing letter never matches.
const TWO_LETTER_SUFFIX = /(?<![A-Za-z])[A-Za-z]{2}$/;

const HEX_COLOR = /^#([0-9A-Fa-f]{6})$/;

export const UNMAPPED_PO_COLOR = '#CBD5E1';
// The pre-mapping green/emerald cycle, indexed by segment position within the bar. Only
// privacy mode uses it now: every role receives the mappings (partners with names redacted).
export const PARTNER_PO_COLORS = ['#86EFAC', '#6EE7B7', '#4ADE80', '#34D399', '#10B981'];

const REDACTED_CUSTOMER_PREFIX = 'Customer';

const DARK_LABEL = 'rgb(15 23 42)';
const LIGHT_LABEL = 'rgb(248 250 252)';
// Perceived-luminance weights (ITU-R BT.601) and the light/dark cutoff.
const RED_WEIGHT = 0.299;
const GREEN_WEIGHT = 0.587;
const BLUE_WEIGHT = 0.114;
const LIGHT_FILL_THRESHOLD = 145;
const HEX_RADIX = 16;
const RED_SHIFT = 16;
const GREEN_SHIFT = 8;
const CHANNEL_MASK = 0xff;

export function extractCustomerCode(poNumber: string): string | null {
  const match = poNumber.match(TWO_LETTER_SUFFIX);
  return match ? match[0].charAt(1).toUpperCase() : null;
}

function partnerColorAt(index: number): string {
  return PARTNER_PO_COLORS[index % PARTNER_PO_COLORS.length];
}

export function resolveCustomerMapping(poNumber: string, mappings: CustomerMapping[]): CustomerMapping | null {
  const code = extractCustomerCode(poNumber);
  if (!code) return null;
  return mappings.find((mapping) => mapping.code === code) ?? null;
}

export function resolvePoSegmentColor(poNumber: string, mappings: CustomerMapping[], options: { hideCustomerMappings: boolean; index: number }): string {
  // Privacy mode swaps every mapped colour for the neutral green cycle so a shared screen
  // can't be read back to customers through the legend colours.
  if (options.hideCustomerMappings) return partnerColorAt(options.index);
  return resolveCustomerMapping(poNumber, mappings)?.color ?? UNMAPPED_PO_COLOR;
}

export function customerLabel(mapping: CustomerMapping): string {
  return mapping.customerName ?? `${REDACTED_CUSTOMER_PREFIX} ${mapping.code}`;
}

export function labelColorFor(fillColor: string): string {
  const match = HEX_COLOR.exec(fillColor);
  if (!match) return DARK_LABEL;
  const value = parseInt(match[1], HEX_RADIX);
  const red = (value >> RED_SHIFT) & CHANNEL_MASK;
  const green = (value >> GREEN_SHIFT) & CHANNEL_MASK;
  const blue = value & CHANNEL_MASK;
  const luminance = red * RED_WEIGHT + green * GREEN_WEIGHT + blue * BLUE_WEIGHT;
  return luminance > LIGHT_FILL_THRESHOLD ? DARK_LABEL : LIGHT_LABEL;
}
